/**
 * GroupTrialBalanceDetail — full trial balance.
 *
 * v3 (hooks-order fix):
 *   - totalDebit / totalCredit useMemos moved ABOVE the early returns. They
 *     were below the `if (isLoading)` / `if (isError)` returns, so the hook
 *     count changed between renders → "Rendered more hooks than during the
 *     previous render" crash on the Trial Balance tab. All hooks now run on
 *     every render, in the same order (Rules of Hooks).
 *
 * v2 (demo polish):
 *   - Defensive `?? 0` on every numeric so `format(undefined)` can't ever
 *     surface NaN to the UI
 *   - Accepts optional `businessId` to scope the trial balance to one
 *     business inside the group
 *   - Header label adapts to scope (Group vs. specific business)
 */
import React, { useMemo } from "react";
import GroupCompletenessNotice, { type GroupReportMeta } from "@/components/group/GroupCompletenessNotice";
import {
  Loader2,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Scale,
  Building2,
} from "lucide-react";
import { useGroupTrialBalance } from "@/hooks/useGroupReports";
import ReportExportMenu from "./ReportExportMenu";
import { useDisplayCurrency } from "@/context/DisplayCurrencyContext";

interface Props {
  asOf: string;
  /** When set, scope the trial balance to a single business. */
  businessId?: string | null;
  /** Display label for the chosen business (or "All businesses"). */
  businessLabel?: string | null;
}

const TYPE_ORDER = ["asset", "liability", "equity", "revenue", "expense"];

const TYPE_LABELS: Record<string, string> = {
  asset: "Assets",
  liability: "Liabilities",
  equity: "Equity",
  revenue: "Revenue",
  expense: "Expenses",
};

/** Safely coerce any value to a number. NaN/undefined/null → 0. */
function num(v: unknown): number {
  const n = typeof v === "number" ? v : Number(v ?? 0);
  return Number.isFinite(n) ? n : 0;
}

const GroupTrialBalanceDetail: React.FC<Props> = ({
  asOf,
  businessId,
  businessLabel,
}) => {
  const { data, isLoading, isError } = useGroupTrialBalance({
    asOf,
    businessId,
  });
  const { format } = useDisplayCurrency();

  const asOfLabel = formatDate(asOf);
  const scopeLabel =
    businessId && businessLabel ? businessLabel : "All businesses";

  // Defensive row hydration — every account is guaranteed numeric
  // debit/credit, even if backend omitted the field.
  const safeRows = useMemo(() => {
    return (data?.rows || []).map((r) => ({
      ...r,
      debitMinor: num(r.debitMinor),
      creditMinor: num(r.creditMinor),
      code: r.code || "—",
      name: r.name || "(unnamed)",
      type: r.type || "other",
    }));
  }, [data?.rows]);

  const grouped = useMemo(() => {
    const out: Record<string, typeof safeRows> = {};
    safeRows.forEach((r) => {
      if (!out[r.type]) out[r.type] = [];
      out[r.type].push(r);
    });
    return out;
  }, [safeRows]);

  // Safe totals — recomputed from rows so the UI never depends on
  // possibly-undefined backend totals.
  //
  // IMPORTANT: these useMemo hooks MUST stay above the early returns below.
  // They depend only on safeRows (already computed), and on the loading render
  // safeRows is [] so the totals are simply 0. Placing them after a conditional
  // return changes the hook count between renders → React crash.
  const totalDebit = useMemo(
    () => safeRows.reduce((s, r) => s + r.debitMinor, 0),
    [safeRows],
  );
  const totalCredit = useMemo(
    () => safeRows.reduce((s, r) => s + r.creditMinor, 0),
    [safeRows],
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="rounded-md border border-destructive/20 bg-destructive/5 p-4 text-sm text-destructive flex items-start gap-2">
        <AlertTriangle className="h-4 w-4 mt-0.5" />
        <div>
          <div className="font-medium">Couldn't load trial balance</div>
          <div className="opacity-80 mt-1">Refresh the page and retry.</div>
        </div>
      </div>
    );
  }

  // Use backend's balanced flag if provided; otherwise derive it.
  // (These are plain consts, not hooks — safe below the returns.)
  const balanced =
    typeof data.balanced === "boolean"
      ? data.balanced
      : totalDebit === totalCredit;
  const difference = Math.abs(totalDebit - totalCredit);

  // ─ Export ─

  const getCsvData = () => {
    const rows: Array<Array<string | number>> = [];
    TYPE_ORDER.forEach((type) => {
      const accounts = grouped[type] || [];
      if (accounts.length === 0) return;
      rows.push([TYPE_LABELS[type] || type.toUpperCase(), "", ""]);
      accounts.forEach((a) => {
        rows.push([
          `  ${a.code} — ${a.name}`,
          a.debitMinor ? format(a.debitMinor) : "",
          a.creditMinor ? format(a.creditMinor) : "",
        ]);
      });
      rows.push(["", "", ""]);
    });
    rows.push(["TOTAL", format(totalDebit), format(totalCredit)]);
    return { headers: ["Account", "Debit", "Credit"], rows };
  };

  const getPdfSections = () => {
    const sections: Array<{
      heading?: string;
      paragraph?: string;
      headers?: string[];
      rows?: Array<Array<string | number>>;
    }> = [];

    sections.push({
      heading: balanced
        ? "Trial balance — Balanced"
        : "Trial balance — NOT BALANCED",
      paragraph: balanced
        ? `Total debits equal total credits. Books balance as of ${asOfLabel} for ${scopeLabel}.`
        : `Warning: total debits ≠ total credits as of ${asOfLabel}. Investigate journal entries.`,
      headers: ["Total", "Amount"],
      rows: [
        ["Total Debits", format(totalDebit)],
        ["Total Credits", format(totalCredit)],
        ["Difference", format(difference)],
      ],
    });

    TYPE_ORDER.forEach((type) => {
      const accounts = grouped[type] || [];
      if (accounts.length === 0) return;
      sections.push({
        heading: TYPE_LABELS[type] || type,
        headers: ["Code", "Account", "Debit", "Credit"],
        rows: accounts.map((a) => [
          a.code,
          a.name,
          a.debitMinor ? format(a.debitMinor) : "—",
          a.creditMinor ? format(a.creditMinor) : "—",
        ]),
      });
    });

    return sections;
  };

  const getShareSummary = () =>
    [
      `Trial Balance — ${scopeLabel}`,
      `As of ${asOfLabel}`,
      ``,
      `Total Debits:  ${format(totalDebit)}`,
      `Total Credits: ${format(totalCredit)}`,
      `Balanced:      ${balanced ? "✓ YES" : "✗ NO — investigate"}`,
      ``,
      `${safeRows.length} accounts across ${Object.keys(grouped).length} types`,
    ].join("\n");

  // ─ Render ─

  return (
    <section className="space-y-5">
      <GroupCompletenessNotice meta={data as GroupReportMeta | undefined} />
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2 mb-0.5">
            <h2 className="text-base font-semibold text-foreground">
              Trial Balance
            </h2>
            <ScopeBadge label={scopeLabel} businessId={businessId} />
          </div>
          <p className="text-xs text-muted-foreground">
            As of {asOfLabel} · {safeRows.length} accounts
          </p>
        </div>
        <ReportExportMenu
          title={`Trial Balance — ${scopeLabel}`}
          subtitle={`As of ${asOfLabel}`}
          getCsvData={getCsvData}
          getPdfSections={getPdfSections}
          getShareSummary={getShareSummary}
        />
      </div>

      {/* Balanced indicator */}
      <div
        className={[
          "rounded-lg border-2 p-4 flex items-center gap-4 flex-wrap",
          balanced
            ? "border-[#4CAF50]/30 bg-[#4CAF50]/5"
            : "border-rose-300 bg-rose-50",
        ].join(" ")}
      >
        <div className="shrink-0">
          {balanced ? (
            <CheckCircle2 className="h-8 w-8 text-[#4CAF50]" />
          ) : (
            <XCircle className="h-8 w-8 text-rose-600" />
          )}
        </div>
        <div className="flex-1 min-w-[200px]">
          <div className="text-base font-semibold">
            {balanced
              ? "Books are balanced"
              : "Books are NOT balanced — investigate"}
          </div>
          <div className="text-xs text-muted-foreground mt-0.5">
            Total debits = Total credits across {safeRows.length} accounts.
          </div>
        </div>
        <div className="grid grid-cols-2 gap-6 shrink-0 text-right">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
              Debits
            </div>
            <div className="text-base font-semibold tabular-nums">
              {format(totalDebit)}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
              Credits
            </div>
            <div className="text-base font-semibold tabular-nums">
              {format(totalCredit)}
            </div>
          </div>
        </div>
      </div>

      {/* Per-type sections */}
      {safeRows.length === 0 ? (
        <div className="rounded-md border bg-muted/30 p-8 text-center text-sm text-muted-foreground">
          No accounts with activity in this period.
        </div>
      ) : (
        TYPE_ORDER.filter((t) => (grouped[t]?.length || 0) > 0).map((type) => (
          <AccountTypeTable
            key={type}
            title={TYPE_LABELS[type] || type}
            accounts={grouped[type]}
            format={format}
          />
        ))
      )}

      {/* Bottom total */}
      {safeRows.length > 0 && (
        <div className="rounded-lg bg-[#0A2540] text-white p-4 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <Scale className="h-4 w-4" />
            <span className="text-sm font-semibold">Grand totals</span>
          </div>
          <div className="grid grid-cols-2 gap-6 text-right">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-white/60 font-semibold">
                Total Debits
              </div>
              <div className="text-lg font-bold tabular-nums">
                {format(totalDebit)}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-white/60 font-semibold">
                Total Credits
              </div>
              <div className="text-lg font-bold tabular-nums">
                {format(totalCredit)}
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

// ─── Helpers ──────────────────────────────────────────────────────

const ScopeBadge: React.FC<{
  label: string;
  businessId?: string | null;
}> = ({ label, businessId }) => (
  <span
    className={[
      "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider",
      businessId
        ? "bg-[#4CAF50]/10 text-[#4CAF50]"
        : "bg-muted text-muted-foreground",
    ].join(" ")}
  >
    <Building2 className="h-2.5 w-2.5" />
    {label}
  </span>
);

const AccountTypeTable: React.FC<{
  title: string;
  accounts: Array<{
    code: string;
    name: string;
    debitMinor: number;
    creditMinor: number;
  }>;
  format: (m: number) => string;
}> = ({ title, accounts, format }) => {
  const debitTotal = accounts.reduce((s, a) => s + num(a.debitMinor), 0);
  const creditTotal = accounts.reduce((s, a) => s + num(a.creditMinor), 0);

  return (
    <div className="rounded-lg border bg-card overflow-hidden">
      <div className="px-4 py-3 border-b bg-muted/40 flex items-center justify-between">
        <h3 className="text-sm font-semibold">{title}</h3>
        <span className="text-[10px] text-muted-foreground">
          {accounts.length} accounts
        </span>
      </div>
      <table className="w-full text-sm">
        <thead className="border-b">
          <tr className="text-left text-[10px] uppercase tracking-wider text-muted-foreground">
            <th className="font-medium py-2 px-4 w-20">Code</th>
            <th className="font-medium py-2 px-4">Account</th>
            <th className="font-medium py-2 px-4 text-right w-32">Debit</th>
            <th className="font-medium py-2 px-4 text-right w-32">Credit</th>
          </tr>
        </thead>
        <tbody>
          {accounts.map((a) => (
            <tr
              key={a.code}
              className="border-b last:border-0 hover:bg-muted/20"
            >
              <td className="py-2 px-4 text-muted-foreground tabular-nums">
                {a.code}
              </td>
              <td className="py-2 px-4">{a.name}</td>
              <td className="py-2 px-4 text-right tabular-nums">
                {a.debitMinor ? (
                  format(a.debitMinor)
                ) : (
                  <span className="text-muted-foreground/40">—</span>
                )}
              </td>
              <td className="py-2 px-4 text-right tabular-nums">
                {a.creditMinor ? (
                  format(a.creditMinor)
                ) : (
                  <span className="text-muted-foreground/40">—</span>
                )}
              </td>
            </tr>
          ))}
          <tr className="bg-muted/30 font-semibold">
            <td className="py-2.5 px-4" />
            <td className="py-2.5 px-4">Subtotal</td>
            <td className="py-2.5 px-4 text-right tabular-nums">
              {format(debitTotal)}
            </td>
            <td className="py-2.5 px-4 text-right tabular-nums">
              {format(creditTotal)}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

export default GroupTrialBalanceDetail;
